import React, { useState, useEffect, useCallback } from "react";
import axios from "../services/httpClient";
import { toast } from "react-toastify";
import { FiX, FiCalendar } from "react-icons/fi";

const AdminStudentActivity = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [limit] = useState(100);

  // Side panel state
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tokenUsage, setTokenUsage] = useState([]);
  const [loadingTokenUsage, setLoadingTokenUsage] = useState(false);

  // Fetch study time data
  const fetchStudyTime = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        date: selectedDate,
        page: currentPage.toString(),
        limit: limit.toString(),
      });

      const response = await axios.get(
        `/progress/admin/study-time?${params.toString()}`
      );

      if (response.data) {
        setUsers(response.data.users || []);
        setTotalCount(response.data.totalCount || 0);
        setTotalPages(response.data.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching study time:", error);
      toast.error("Failed to load student activity. Please try again.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, currentPage, limit]);

  // Fetch token usage for selected user
  const fetchTokenUsage = useCallback(async (userId, date) => {
    setLoadingTokenUsage(true);
    setTokenUsage([]); // Clear previous data
    try {
      const params = new URLSearchParams({
        userId: userId,
        date: date,
      });

      const response = await axios.get(
        `/progress/admin/user-token-usage?${params.toString()}`
      );

      console.log("Token usage response:", response.data);

      if (response.data) {
        // Parse the response - check for records array first
        const usageData =
          response.data.records ||
          (Array.isArray(response.data) ? response.data : []) ||
          response.data.usage ||
          response.data.data ||
          [];

        console.log("Parsed usage data:", usageData);

        const processedData = usageData.map((item) => {
          const inputTokens = item.inputTokenCount || item.input_tokens || 0;
          const outputTokens = item.outputTokenCount || item.output_tokens || 0;
          const ttsCost = item.tts_cost || 0;

          // Calculate LLM cost: (0.30 * total_input_tokens + 2.50 * total_output_tokens) * 90 / 1000000
          const llmCost =
            ((0.3 * inputTokens + 2.5 * outputTokens) * 90) / 1000000;

          // Total cost = LLM cost + TTS cost
          const totalCost = llmCost + ttsCost;

          return {
            type: item.type || "N/A",
            inputTokenCount: inputTokens,
            outputTokenCount: outputTokens,
            tts_cost: ttsCost,
            totalCost: totalCost,
            llmCost: llmCost,
          };
        });

        console.log("Processed token usage data:", processedData);
        setTokenUsage(processedData);
      } else {
        console.log("No data in response");
        setTokenUsage([]);
      }
    } catch (error) {
      console.error("Error fetching token usage:", error);
      toast.error("Failed to load token usage data. Please try again.");
      setTokenUsage([]);
    } finally {
      setLoadingTokenUsage(false);
    }
  }, []);

  useEffect(() => {
    fetchStudyTime();
  }, [fetchStudyTime]);

  const handleRowClick = (user) => {
    setSelectedUser(user);
    setIsSidePanelOpen(true);
    fetchTokenUsage(user.userId, selectedDate);
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setCurrentPage(1); // Reset to first page when date changes
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(amount);
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        Student Activity
      </h1>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <FiCalendar className="w-4 h-4" />
            Date:
          </label>
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="mt-2 text-gray-500">Loading...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No activity data found for the selected date.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Minutes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user, index) => (
                    <tr
                      key={user.userId || index}
                      onClick={() => handleRowClick(user)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.email || user.userId || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {user.minutes
                          ? Math.round(user.minutes * 10) / 10
                          : "0"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(user.date)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {users.length} of {totalCount} users
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1 || loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages || loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Side Panel for Token Usage */}
      {isSidePanelOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setIsSidePanelOpen(false)}
          ></div>

          {/* Panel */}
          <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  Token Usage Activity
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedUser?.email || "N/A"} - {formatDate(selectedDate)}
                </p>
              </div>
              <button
                onClick={() => setIsSidePanelOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingTokenUsage ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <p className="mt-2 text-gray-500">Loading token usage...</p>
                </div>
              ) : tokenUsage.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No token usage data available for this user and date.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Total cost calculation uses USD to
                      INR conversion rate of 90. LLM cost = (0.30 × input tokens
                      + 2.50 × output tokens) × 90 / 1,000,000
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Input Tokens
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Output Tokens
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            TTS Cost (INR)
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Cost (INR)
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tokenUsage.map((usage, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {usage.type}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {usage.inputTokenCount.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {usage.outputTokenCount.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {formatCurrency(usage.tts_cost)}
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                              {formatCurrency(usage.totalCost)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                        <tr>
                          <td
                            colSpan="4"
                            className="px-4 py-3 text-sm font-bold text-gray-700"
                          >
                            Grand Total:
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-gray-900">
                            {formatCurrency(
                              tokenUsage.reduce(
                                (sum, usage) => sum + usage.totalCost,
                                0
                              )
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStudentActivity;
